import React, { useCallback, useRef, useState } from "react";

import { Autorenew } from "@mui/icons-material";
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import StatusButton from "../components/common/StatusButton";
import PrimaryLayout from "../components/layout/PrimaryLayout";
import DataTable from "../components/tables/DataTable";
import { Column } from "../components/tables/interfaces";
import { axiosRepoIntance } from "../services/api-client";
import { Branch, CommitStatus } from "../services/repo-service";
import { OWNER } from "../utils/constants/gitea";
import AddDeployment from "../views/deployments/AddDeployment";


export interface CopyToNamespaceContent {
  name: string;
  version: string;
  namespace: string[];
  commitId: string;
}

const columns: Column[] = [
  { id: "name", label: "Name", type: "string" },
  { id: "id", label: "Commit ID", type: "string" },
  { id: "status", label: "Status", type: "string" },
  { id: "namespace", label: "Namespace", type: "string" },
  { id: "author", label: "Author", type: "string" },
  { id: "created_at", label: "Created At", type: "datetime" },
  { id: "actions", label: "Actions" },
];


const Deployments: React.FC = () => {

  const statusMap = useRef<Record<string, any>>({});

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [copyToNamespaceContent, setCopyToNamespaceContent] = useState<CopyToNamespaceContent>({
    name: '',
    version: '',
    namespace: [],
    commitId: '',
  });

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setCopyToNamespaceContent({ name: '', version: '', namespace: [], commitId: '' });
  }

  const fetchData = useCallback(async (query?: | { pageSize?: number; nextPageToken?: string } | { page?: number; limit?: number }) => {
    if (query && ("page" in query || "limit" in query)) {
      try {
        const response = await axiosRepoIntance.repos.repoListBranches(
          OWNER,
          "deployments",
          query
        );
        console.log(response.data);
        const branches = response.data;

        await Promise.all(
          branches.map(async (branch) => {
            let temp: CommitStatus[] = [];
            let deploymentStatus = "No Commits Found";
            if (branch.commit?.id) {
              try {
                temp = await fetchRunnerStatus(branch.commit.id, "deployments");
                const status = temp.length > 0 ? temp[0].status : undefined;
                const des = temp.length > 0 ? temp[0].description : "";
                deploymentStatus = status || "No Commits Found";
                if (deploymentStatus === "success" && des === "Has been skipped") {
                  deploymentStatus = "failure"
                }
              } catch (error) {
                console.error("Error fetching runner status:", error);
              }

              statusMap.current[branch.commit.id] = (
                temp.length > 0 ? <StatusButton
                  deploymentStatus={deploymentStatus}
                  context={temp[0].context ? temp[0].context : ""}
                  createdAt={temp[0].created_at ? temp[0].created_at : ""}
                  updatedAt={temp[0].updated_at ? temp[0].updated_at : ""}
                /> :
                  <Typography variant="body2" color="textSecondary">
                    No Status available, Please refresh.
                  </Typography>
              );
            }
          })
        );

        return branches;
      } catch (error) {
        console.error("Error fetching blocks:", error);
        return [] as Branch[];
      }
    } else {
      return [] as Branch[];
    }
  }, []);

  const dataExtractor = useCallback((response: Branch[]): any[] => {
    return (response || []).map((branch) => {
      return {
        id: branch.commit?.id,
        name: branch.name,
        status: statusMap.current[branch.commit?.id || ''],
        namespace:
          <Box>
            {(branch.commit?.message || "").trim().split('namespace:').reverse()[0].trim().split(',').map((item) => (
              <Box key={item} whiteSpace='nowrap'>- {item}</Box>
            ))}
          </Box>,
        author: branch.commit?.author?.name,
        created_at: branch.commit?.timestamp,
        actions:
          <Box display='flex' gap={1}>
            <Tooltip title='Deploy to other Namespace'>
              <IconButton color='warning' onClick={() => {
                console.log('clicked branch details: ', branch);
                const dName = branch.name?.split('_');

                setCopyToNamespaceContent({
                  name: dName ? `${dName[0]}/${dName[1]}` : '',
                  version: dName ? `${dName[2]}` : '',
                  namespace: branch.commit?.message?.trim().split('namespace:').reverse()[0].trim().split(',') || ['default'],
                  commitId: branch.commit?.id || '',
                });

                setOpenDrawer(true);
              }}><Autorenew /></IconButton>
            </Tooltip>
          </Box>
      }
    });
  }, []);

  const fetchRunnerStatus = async (
    commit_id: string,
    branch_name: string,
    query?: {
      sort?: "oldest" | "recentupdate" | "leastupdate" | "leastindex" | "highestindex";
      page?: number;
      limit?: number;
    }
  ) => {
    try {
      const response = await axiosRepoIntance.repos.repoListStatusesByRef(
        OWNER,
        branch_name,
        commit_id,
        {
          sort: query?.sort || "leastindex",
          page: query?.page || 1,
          limit: query?.limit || 1,
        }
      );
      console.log(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching runner statuses:", error);
      return [];
    }
  };

  return (
    <PrimaryLayout transparentLoading={isLoading}>
      <Box sx={{ mx: 2, my: 1 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Deployments
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Promote your workflow and block versions across environments by deploying to different namespaces.
          Track deployment status, manage rollouts, and replicate deployments with full visibility and control.
        </Typography>
      </Box>
      {/* <Box display="flex" justifyContent="space-between" alignItems="center" m={2}> */}
      <Box display="flex" gap={2} sx={{ mx: 2 }}>
        <Button
          variant="contained"
          color='secondary'
          startIcon={<RefreshIcon />}
          onClick={() => setRefreshKey(prevKey => prevKey + 1)}
        >
          Refresh
        </Button>

        <Button onClick={() => setOpenDrawer(true)} variant="contained" color="primary" startIcon={<AddIcon />} >
          Add Deployment
        </Button>
      </Box>
      {/* </Box> */}

      {openDrawer && <AddDeployment
        open={openDrawer}
        copyToNamespaceContent={copyToNamespaceContent}
        setRefreshKey={setRefreshKey}
        setIsLoading={setIsLoading}
        onClose={handleDrawerClose}
      />}

      <Box key={refreshKey}>
        <DataTable
          columns={columns}
          fetchData={fetchData}
          dataExtractor={dataExtractor}
          stickyHeader
          tableContainerMaxHeight={'calc(100vh - 180px)'}
        />
      </Box>
    </PrimaryLayout >
  );
};

export default Deployments;